import type {
  Block, Cell, ChipCell, Column, NoteBlock, TableBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import { jurisdictionControls } from './jurisdictioncontrols'
import { CAPABILITY_SET, JURISDICTIONS_PATH, jurisdictionPath } from './jurisdictionvocab'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside } from './writes'

/**
 * Re-exported so the read module stays the one import a caller needs for the
 * screen, while the vocabulary keeps exactly one definition.
 *
 * Straight from `jurisdictionvocab` rather than re-listing the local bindings:
 * `KNOWN_CAPABILITIES` is not imported here — nothing in this file reads it,
 * only `CAPABILITY_SET` — and naming it in a bare `export { }` is a reference
 * to a binding that does not exist.
 */
export { KNOWN_CAPABILITIES, JURISDICTIONS_PATH, jurisdictionPath } from './jurisdictionvocab'

/**
 * Configuration › Jurisdictions & country gating, read from
 * `GET /v1/admin/jurisdictions` and `GET /v1/admin/jurisdictions/{country}`
 * (go-wallet#301).
 *
 * # This screen is the read side of invariant 7, and closed is three states
 *
 * Country gating fails closed for three different reasons and the backend goes
 * to deliberate trouble not to collapse them: **no jurisdiction row at all**,
 * **a row whose `onboarding_open` is false**, and **a capability absent from
 * `mandate_scope`**. `gateState` is a closed set on the wire for exactly that
 * reason — the handler's own comment says the derivation is where a client gets
 * it wrong, because `!open` reads as "somebody closed this" for a country
 * nobody ever declared.
 *
 * So the three never become one "enabled" here either. `gate` is rendered as
 * the backend sent it, `onboarding_open` stays its own column beside it, and
 * the capabilities a country's mandate does NOT cover are named rather than
 * left to be noticed by their absence from a list. An operator shown "closed"
 * without knowing which of the three cannot act on it: one is chased with a
 * passport notification, one is a decision somebody took and can undo, and one
 * is a mandate we never held.
 *
 * # Absence from this table is a fact, not an oversight
 *
 * `storage.JurisdictionReader.List` returns only the rows that exist, and that
 * is correct — a row per ISO country would invent 249 assertions nobody made.
 * But a short list read without that context looks like a list somebody forgot
 * to finish. The table says so in its own footer instead of leaving it to be
 * inferred, and `no_mandate` is a first-class row shape here so that a country
 * we hold nothing in renders as *we hold nothing there* wherever it appears.
 *
 * # Why the passport notification is on screen
 *
 * `adminops.setOpen` refuses an open outright when `passport_notified_at` is
 * null (`ErrNoPassportNotification`), and the schema CHECK refuses it again
 * underneath. The payload carries `passport_notified` precisely so an operator
 * does not attempt an open that is going to be refused. Surfacing it turns a
 * wasted attempt into a known next action: file the notification.
 *
 * The converse is NOT stated. A country with a notification on file is a
 * country whose *named* precondition is met — this console has not verified
 * that an open would succeed, and a chip reading "may be opened" would be a
 * reassurance nobody checked.
 *
 * # This file reads; the controls are next to it (#213)
 *
 * This section used to say there was no open control and no close control here,
 * and that a read adapter rendering an action affordance would be claiming an
 * authority it had not been given. The first half is corrected rather than
 * deleted, because it is the sentence a reader would have trusted instead of
 * re-checking, and because what was true about it still is.
 *
 * What made it stale was not a change of mind. It was #213: milen asked for
 * Spain and Bulgaria to be opened, the console offered no way to do it, and the
 * act was performed by exec-ing `wallet-admin` inside a staging pod. Staging
 * held exactly one jurisdiction row. A console that renders the fail-closed
 * control and cannot operate it sends the operator to a shell, which is where
 * `adminops/jurisdiction.go` says the first act in the life of every
 * jurisdiction used to happen — unaudited, by whoever had a database prompt.
 *
 * The distinction the old paragraph was reaching for survives intact and is now
 * enforced rather than asserted: **this file still reads.** Every write lives in
 * `jurisdictioncontrols.ts`, is permissioned on `jurisdiction.write` and shown
 * disabled with the Compliance role named where it is not held (law 18), and
 * reaches this module only through `ControlBuilder` — a function handed the rows
 * that were parsed, which is why no control can offer a country this table
 * failed to read.
 *
 * # And nothing on this screen is money
 *
 * There is no `amount_minor`, no balance and no amount cell anywhere in the
 * jurisdiction DTO, so there is no `formatMinor` call to guard. `account_currency`
 * is the one currency on the surface and it is NOT an amount: it is the
 * denomination every account a country issues will carry, written once at
 * declaration and never rendered beside a number.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_jurisdiction_read.go json tags
// ---------------------------------------------------------------------------

/**
 * The gate history, from the audit log.
 *
 * `reason` is the only `omitempty` field. The rest are emitted unconditionally,
 * which is why an absent one below is recorded as a gap rather than shrugged
 * off as "does not apply".
 */
export interface GateChangeView {
  readonly actor?: string
  /** `jurisdiction.opened` | `jurisdiction.closed` | `jurisdiction.created`. */
  readonly action?: string
  /** Go `omitempty`: absent when the audit row's attrs carried no reason. */
  readonly reason?: string
  readonly at?: string
}

/**
 * One country's gate.
 *
 * Every field is optional here even though Go emits most of them
 * unconditionally, because the parse below trusts nothing: a field declared
 * required is a field that will happily read as `undefined` at runtime and
 * render as the string "undefined".
 */
export interface JurisdictionView {
  readonly country?: string
  /** `open` | `closed` | `no_mandate`. The field to branch on, never derived. */
  readonly gate?: string
  readonly onboarding_open?: boolean
  /** Never nil from Go — `[]` is a real read meaning nothing is permitted. */
  readonly mandate_scope?: readonly string[]
  readonly passport_notified?: boolean
  /** Go `omitempty`: absent when no notification is on file. */
  readonly passport_notified_at?: string
  /** Go `omitempty` on a pointer. See `readLastChange` — absent is ambiguous. */
  readonly last_change?: GateChangeView
}

export interface JurisdictionListResponse {
  readonly jurisdictions?: readonly JurisdictionView[]
}

const SOURCE =
  'postgres · jurisdictions + audit_log (GET /v1/admin/jurisdictions, GET /v1/admin/jurisdictions/{country})'

/**
 * How many per-country history reads one screen load will issue.
 *
 * Each one is a separately permissioned, separately access-logged admin read,
 * so a list of every ISO country would turn one screen load into 249 of them.
 * Bounded rather than unbounded — and the rows beyond the bound say "not
 * requested" rather than quietly showing no history, because an empty history
 * column and an unread one are the same two sentences this whole file is about.
 */
export const DETAIL_READ_CAP = 64

/**
 * One endpoint's answer, or the account of why there is none.
 *
 * `composeJurisdictions` takes these rather than parsed payloads so that the
 * interesting half of this file — what the screen does when the list read and
 * the history reads disagree about how well they went — is table-testable with
 * no client and no mocked rejection.
 */
export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      /** Never synthesised. `null` means the backend sent none. */
      readonly decisionRef: string | null
      /** A 403. Distinguished so the note can say a refusal came without a ref. */
      readonly refused: boolean
    }

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

/**
 * `Array.isArray` narrows `unknown` to `any[]`, which then spreads `any`
 * through everything it touches. Landing it back on `readonly unknown[]` keeps
 * every element a thing the parse has to prove something about.
 */
function asArray(raw: unknown): readonly unknown[] | null {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : null
}

function readText(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readBool(o: Record<string, unknown>, key: string): boolean | null {
  const raw = o[key]
  return typeof raw === 'boolean' ? raw : null
}

/**
 * Whether a vocabulary this console owns really contains a word off the wire.
 *
 * `word in TABLE` walks the prototype chain, so `constructor`, `toString`,
 * `valueOf` and `__proto__` all answer true against any object literal — and
 * the lookup that follows returns a FUNCTION, not a tone. An unrecognised gate
 * would come back "recognised", chip itself with `Object.prototype.toString`
 * in place of a tone, and record no gap, so the screen would badge itself live
 * over a regulatory state it does not know. Same guard, same reason, as
 * `reconcilerCard` in `dashboard.ts`.
 */
function knows(table: Readonly<Record<string, unknown>>, word: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, word)
}

type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * An RFC-3339 instant, in the format the design draws timestamps in.
 *
 * The epoch guard is load-bearing on this endpoint specifically. The SQL stores
 * "no passport notification" as `'epoch'::timestamptz` and `List` converts it
 * back to a zero time with a year check — so a value that slipped past that
 * conversion arrives as `1970-01-01T00:00:00Z`, parses perfectly, and renders
 * as a date on which the regulator was supposedly notified. Rejecting
 * `getTime() <= 0` turns the sentinel back into an absence, which is what it
 * means. The same guard catches Go's `0001-01-01T00:00:00Z` zero time.
 */
function readInstant(o: Record<string, unknown>, key: string): InstantRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

function normaliseCountry(raw: string): string {
  return raw.trim().toUpperCase()
}

/** The shape the backend's `isAlpha2` accepts. Anything else is a guaranteed 400. */
function isAlpha2(code: string): boolean {
  return /^[A-Z]{2}$/.test(code)
}

/** Groups repeated gaps so the provenance chip stays readable at 40 rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) => `${label} (${String(n)} ${n === 1 ? 'row' : 'rows'})`)
}

function list(codes: readonly string[]): string {
  return codes.join(', ')
}

// ---------------------------------------------------------------------------
// The vocabularies, each closed on the backend
// ---------------------------------------------------------------------------

type GateState = 'open' | 'closed' | 'no_mandate'

const GATE_LABEL: Readonly<Record<string, string>> = {
  open: 'open',
  closed: 'closed',
  no_mandate: 'no mandate',
}

/**
 * Gate tone.
 *
 * `closed` is attention rather than critical, and `no_mandate` is neutral
 * rather than either. Both are the fail-closed state working correctly — a
 * country we never declared is the normal condition of roughly two hundred of
 * them — and painting the safe state red would make this screen red nearly
 * everywhere, which is how a console teaches its operators that red means
 * nothing (`provenance.ts` makes the same argument about the corpus badge).
 *
 * The genuinely alarming states on this screen are not gate values at all:
 * open with no passport notification, and open with an empty mandate. Those
 * get the critical tone, in the note below, where there is room to say why.
 */
const GATE_TONE: Readonly<Record<string, Tone>> = {
  open: 'g',
  closed: 'a',
  no_mandate: 'n',
}

/**
 * The Phase-1 capabilities, from `internal/jurisdiction`'s closed set.
 *
 * Named separately from the full vocabulary because these are the four the
 * product actually reaches today, and "not covered" is only a useful sentence
 * about a capability that exists. Listing `crypto_ramp` as absent for every
 * country would bury the one absence that matters under four that are expected.
 *
 * `internal_transfer` is deliberately NOT here even though it is a real
 * capability the backend parses. `mny-internal` is Phase 2 and no jurisdiction
 * row lists it, so putting it in this list would print "not covered:
 * internal_transfer" on every country in the table — the exact burial the
 * paragraph above is about, and over a capability nobody is waiting on.
 */
const PHASE1_CAPABILITIES = ['receive', 'cash_out', 'storefront', 'statements'] as const

/*
 * The capability vocabulary lives in `jurisdictionvocab.ts` and is imported
 * above. It is shared with the write controls, which is the point: see that
 * module for why a second copy is the bug and what it already cost once.
 */

/** The three audit actions `LastGateChange` selects on. */
const ACTION_LABEL: Readonly<Record<string, string>> = {
  'jurisdiction.opened': 'opened',
  'jurisdiction.closed': 'closed',
  'jurisdiction.created': 'declared',
}

// ---------------------------------------------------------------------------
// The gate history, from the per-country read
// ---------------------------------------------------------------------------

interface ChangeRead {
  readonly cell: Cell
  readonly gaps: readonly string[]
}

/**
 * One country's last gate movement, or the account of why there is none on
 * screen.
 *
 * # Absent is ambiguous, and the console must not resolve it
 *
 * `handleJurisdictionRead` logs a failed `LastGateChange` and carries on, so
 * "the audit query errored" and "this gate has never moved" both reach the wire
 * as an omitted `last_change`. Rendering the omission as "never moved" would
 * state, on a compliance screen, that nobody has ever opened or closed a market
 * — on the strength of a query that may simply have failed. It reads as "not
 * stated" instead, and the gap is counted so the badge stops saying `Live`.
 *
 * That is a backend gap worth an issue on go-wallet, not something to paper
 * over here: the fix is a third state on the wire, the way `availability` does
 * it on the Today board.
 */
function readLastChange(country: string | null, answer: Attempt | undefined): ChangeRead {
  if (country === null) {
    // The unusable country code is already a gap on this row; not counting it
    // twice keeps the chip readable.
    return {
      cell: ['not read', 'this row carried no country code this console could look a history up by'],
      gaps: [],
    }
  }
  if (answer === undefined) {
    return {
      cell: ['not read', 'the per-country history was not requested for this row'],
      gaps: ['a gate history that was not requested'],
    }
  }
  if (!answer.ok) {
    return {
      cell: ['not read', answer.reason],
      gaps: ['a gate history the backend did not answer for'],
    }
  }

  const d = asRecord(answer.payload)
  if (d === null) {
    return {
      cell: ['not read', 'the per-country read returned a shape this console does not know'],
      gaps: ['a gate history in a shape this console does not know'],
    }
  }

  // The answer has to be about this country. Two reads happened; a mismatch
  // means one of them is about somebody else's market, and attributing another
  // country's closure to this row is worse than showing no history at all.
  const answered = readText(d, 'country')
  if (answered === null || normaliseCountry(answered) !== country) {
    return {
      cell: ['not read', `the per-country read answered for ${answered ?? 'no country at all'}`],
      gaps: ['a gate history that answered for a different country'],
    }
  }

  const raw = d['last_change']
  if (raw === undefined || raw === null) {
    return {
      cell: ['not stated', 'omitted whether the gate has never moved or the audit read failed'],
      gaps: ['a gate history the payload can account for'],
    }
  }
  const change = asRecord(raw)
  if (change === null) {
    return {
      cell: ['not read', 'last_change arrived in a shape this console does not know'],
      gaps: ['a gate history in a shape this console does not know'],
    }
  }

  const gaps: string[] = []

  const actionRaw = readText(change, 'action')
  const known = actionRaw !== null && knows(ACTION_LABEL, actionRaw)
  if (!known) {
    gaps.push(actionRaw === null
      ? 'an action on a gate change'
      : 'a gate-change action this console does not know')
  }
  const action = known
    ? (ACTION_LABEL[actionRaw ?? ''] ?? actionRaw ?? '')
    : (actionRaw ?? 'action not stated')

  const when = readInstant(change, 'at')
  if (when.kind !== 'at') gaps.push('a timestamp on a gate change')

  const actor = readText(change, 'actor')
  if (actor === null) gaps.push('an actor on a gate change')

  // `reason` is `omitempty`, and its absence means different things per action.
  // The HTTP open and close routes both run `takeReason`, which refuses a blank
  // — so an opened or closed row with no reason is an anomaly. A `created` row
  // is written by the wallet-admin CLI, whose `Create` does not validate the
  // reason, so an absent one there is a fact about how the market was declared
  // rather than something that went missing.
  const reason = readText(change, 'reason')
  if (reason === null
    && (actionRaw === 'jurisdiction.opened' || actionRaw === 'jurisdiction.closed')) {
    gaps.push('a reason on a gate change')
  }

  return {
    cell: [
      `${action} · ${when.kind === 'at' ? when.label : 'time not stated'}`,
      `${actor ?? 'actor not stated'} — ${reason ?? 'no reason recorded'}`,
    ],
    gaps,
  }
}

// ---------------------------------------------------------------------------
// One country as a row
// ---------------------------------------------------------------------------

interface RowRead {
  readonly cells: readonly Cell[]
  /** The normalised alpha-2 code, or `null` when it could not be read. */
  readonly country: string | null
  /** What the backend said the gate is, or `null` when this console cannot say. */
  readonly gate: GateState | null
  /** `null` when the payload did not let this console decide. Never assumed. */
  readonly notificationOnFile: boolean | null
  /** Open, with no notification: a state the schema CHECK is supposed to refuse. */
  readonly openWithoutNotification: boolean
  /** Open, with a mandate covering nothing: onboarding accepted, everything refused. */
  readonly openWithEmptyMandate: boolean
  /** The notification date as drawn, or `''`. For labelling a control's options. */
  readonly notifiedLabel: string
  /** The capability names that were READ, in wire order. `null` when unreadable. */
  readonly scope: readonly string[] | null
  /** What this row could not say, in operator words, one entry per gap. */
  readonly gaps: readonly string[]
}

/**
 * One country, as a control needs to know it.
 *
 * The public subset of `RowRead`: enough to decide which act is this country's
 * remedy and to label it in a select, and nothing else. The cells stay private
 * because a control that read them would be deciding from rendered strings.
 */
export type GateRow = Pick<
  RowRead, 'country' | 'gate' | 'notificationOnFile' | 'notifiedLabel' | 'scope'
>

/**
 * What the write controls are, given the countries that were read.
 *
 * A FUNCTION rather than a list of blocks, because every control on this screen
 * is gated on row state: only a closed country can be opened, only an open one
 * closed, and only a country with a row at all can have its mandate moved. The
 * builder is handed the rows this compose actually parsed, so a control can
 * never offer a country the table did not manage to read.
 *
 * Optional, and the default is no controls — `composeJurisdictions` stays the
 * pure, clock-free read composition it was, table-testable with no client and
 * no session (CLAUDE.md Part III). `loadJurisdictions` supplies the builder;
 * `theLoaderWiresTheControls` in the tests is what stops that being forgotten.
 */
export type ControlBuilder = (rows: readonly GateRow[]) => readonly Block[]

/**
 * One jurisdiction as a row, or `null` when the element is not an object.
 *
 * A `null` is counted by the caller and stated on the screen. It is never
 * dropped: a gating table that silently renders 6 of 7 countries is a table an
 * operator will read as complete, and the one it lost is a market whose state
 * nobody checked.
 */
function readJurisdiction(raw: unknown, details: ReadonlyMap<string, Attempt>): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null

  const gaps: string[] = []

  // --- country --------------------------------------------------------------
  const countryRaw = readText(o, 'country')
  if (countryRaw === null) gaps.push('a country code')
  const code = countryRaw === null ? null : normaliseCountry(countryRaw)
  if (code !== null && !isAlpha2(code)) {
    gaps.push('a country code shaped like an ISO-3166 alpha-2 code')
  }
  const country = code !== null && isAlpha2(code) ? code : null

  // --- gate, cross-checked against the raw flag -----------------------------
  //
  // `viewOfJurisdiction` derives `gate` FROM `onboarding_open`, so the two
  // cannot legitimately disagree. If they do, one of them is lying and this
  // console has no way to tell which — so it claims neither. Resolving the
  // disagreement in favour of "open" would be resolving it in favour of the
  // reading that permits onboarding, which is the one direction invariant 7
  // forbids guessing in.
  const gateRaw = readText(o, 'gate')
  const flag = readBool(o, 'onboarding_open')
  if (flag === null) gaps.push('an onboarding_open flag')

  const gateKnown = gateRaw !== null && knows(GATE_LABEL, gateRaw)
  if (!gateKnown) {
    gaps.push(gateRaw === null ? 'a gate state' : 'a gate state this console does not know')
  }
  const contradicted = gateKnown && flag !== null && flag !== (gateRaw === 'open')
  if (contradicted) {
    gaps.push(`a gate that agrees with its own onboarding_open flag (${gateRaw ?? ''} beside ${String(flag)})`)
  }

  const gate: GateState | null = gateKnown && !contradicted ? (gateRaw as GateState) : null
  const gateCell: ChipCell = contradicted
    ? ['contradicted', 'r']
    : gate !== null
      ? [GATE_LABEL[gate] ?? gate, GATE_TONE[gate] ?? 'r']
      : [gateRaw ?? 'not stated', 'r']

  // `false` is neutral, not critical. A closed country is the fail-closed
  // default doing its job; only an unreadable flag is an alarm here.
  const openCell: ChipCell = flag === null
    ? ['not stated', 'r']
    : flag ? ['true', 'g'] : ['false', 'n']

  // --- passport notification -------------------------------------------------
  const notified = readBool(o, 'passport_notified')
  const notifiedAt = readInstant(o, 'passport_notified_at')
  if (notified === null) gaps.push('a passport_notified flag')

  let notificationOnFile: boolean | null = null
  let notifiedCell: string
  if (notified === true && notifiedAt.kind === 'at') {
    notificationOnFile = true
    notifiedCell = notifiedAt.label
  } else if (notified === true) {
    // Go sets the flag and the date together. One without the other means the
    // date did not survive, and a notification whose date we cannot state is
    // not one this console will report as on file.
    gaps.push('a notification date on a country flagged as notified')
    notifiedCell = '—'
  } else if (notified === false && notifiedAt.kind === 'absent') {
    notificationOnFile = false
    notifiedCell = '— none'
  } else if (notified === false) {
    gaps.push('a notification date and flag that agree with each other')
    notifiedCell = '—'
  } else {
    notifiedCell = notifiedAt.kind === 'at' ? notifiedAt.label : '—'
  }

  // --- mandate scope ---------------------------------------------------------
  const scope = asArray(o['mandate_scope'])
  let mandateCell: string
  // Read AND empty, which is not the same as read and unrecognised. A scope of
  // `['wire_transfer']` covers nothing this console can evidence, but calling it
  // "an empty mandate_scope" in the note below would describe a payload that
  // does not exist and send an operator looking for a row with no capabilities.
  let emptyMandate = false
  // The capability names this parse actually got hold of, for the controls.
  // `null` while the scope is unreadable, because a narrowing offered over a
  // list we could not read would offer to withdraw capabilities from a set that
  // is not the country's.
  let readScope: readonly string[] | null = null
  if (scope === null) {
    gaps.push('a mandate scope')
    mandateCell = '—'
  } else {
    const named: string[] = []
    const covered = new Set<string>()
    let unnamed = 0
    let unrecognised = 0
    for (const entry of scope) {
      if (typeof entry !== 'string' || entry === '') {
        unnamed += 1
        continue
      }
      named.push(entry)
      // Only a capability from the closed set counts as covering anything. A
      // string this console does not know is shown verbatim and grants nothing
      // — an unrecognised capability resolving to "covered" would report a
      // mandate we cannot evidence.
      //
      // Counted, not inferred from `named.length !== covered.size`: a scope
      // that repeats a capability would fail that comparison and report an
      // unknown capability that is not there.
      if (CAPABILITY_SET.has(entry)) covered.add(entry)
      else unrecognised += 1
    }
    if (unnamed > 0) gaps.push('a mandate scope entry that was not a capability name')
    if (unrecognised > 0) gaps.push('a capability this console does not know')

    readScope = named
    emptyMandate = named.length === 0 && unnamed === 0
    if (named.length === 0) {
      // `[]` is a real read: Go never sends nil here. "Nothing is permitted"
      // is the third closed state, stated rather than left as a blank cell.
      mandateCell = unnamed === 0 ? 'none — nothing is permitted here' : '—'
    } else if (unnamed > 0) {
      // What a mandate does NOT cover is a claim, and it cannot be made from a
      // list this console only partly read. "not covered: cash_out" over a
      // scope carrying one unreadable entry tells an operator that a cash-out
      // refusal here is expected and correct — on the strength of a list that
      // may well have named cash_out in the entry we dropped. The capabilities
      // that WERE read are still shown; only the negative is withheld, because
      // the negative is the half that was not established.
      mandateCell = `${named.join(', ')} · ${String(unnamed)} entr${unnamed === 1 ? 'y' : 'ies'} `
        + 'could not be read, so what this mandate does not cover cannot be stated'
    } else {
      const absent = PHASE1_CAPABILITIES.filter((c) => !covered.has(c))
      mandateCell = absent.length === 0
        ? named.join(', ')
        : `${named.join(', ')} · not covered: ${absent.join(', ')}`
    }
  }

  // --- what an open would meet, and what it would not ------------------------
  //
  // Never "may be opened": `adminops.setOpen` has preconditions beyond the
  // notification and the write is separately permissioned, so the only claim
  // supportable from this payload is about the one precondition it reports.
  //
  // For a country that is ALREADY open this column is the row's verdict rather
  // than a precondition, and the verdict must not be calm on the two states the
  // fail-closed note below calls critical. "already open" in the good tone,
  // beside a green gate and a green flag, made an OPEN country with no passport
  // notification the most reassuring-looking row on the screen — three green
  // chips on a licence contradiction, with the only alarm in a note underneath.
  // The table is the surface an operator scans; the row has to carry it.
  //
  // The gate chip stays green deliberately (see GATE_TONE): "open" really is
  // the normal state, and reddening it would redden the common case. This
  // column reddens only the anomalous rows, which is what tone is for.
  const openVerdict: ChipCell =
    notificationOnFile === false
      ? ['OPEN — no notification', 'r']
      : emptyMandate
        ? ['OPEN — empty mandate', 'r']
        : notificationOnFile === null
          // Open, and this console cannot say whether the licence basis for it
          // is on file. Not green: an unverified basis is not a met one.
          ? ['open — notification unknown', 'a']
          : ['already open', 'g']

  const precondition: ChipCell =
    gate === 'open' ? openVerdict
      : gate === 'no_mandate' ? ['no row to open', 'n']
        : gate === 'closed'
          ? (notificationOnFile === false
              ? ['BLOCKED — no notification', 'r']
              : notificationOnFile === true
                ? ['notification on file', 'a']
                : ['not known', 'r'])
          : ['not known', 'r']

  const history = readLastChange(country, country === null ? undefined : details.get(country))
  gaps.push(...history.gaps)

  return {
    cells: [
      countryRaw ?? '—',
      gateCell,
      openCell,
      notifiedCell,
      mandateCell,
      precondition,
      history.cell,
    ],
    country,
    gate,
    notificationOnFile,
    openWithoutNotification: gate === 'open' && notificationOnFile === false,
    openWithEmptyMandate: gate === 'open' && emptyMandate,
    notifiedLabel: notifiedAt.kind === 'at' ? notifiedAt.label : '',
    scope: readScope,
    gaps,
  }
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

/**
 * The columns, drawn from what the endpoint can actually answer.
 *
 * The corpus draws Country / onboarding_open / passport_notified_at /
 * mandate_scope[] / Customers / Open action. `Customers` has no field on this
 * wire — the gating read is about mandate, not population — so it is gone
 * rather than filled with a plausible zero, which on this screen would read as
 * "closing this market costs nothing".
 *
 * Two columns are new and are the point of the endpoint. `Gate` is the closed
 * set the backend added so a console would stop deriving the state from a bool
 * and a null. `Last gate change` is who moved it, when and why — already
 * required on the way in, and previously readable only in the audit log, which
 * means nobody read it before acting.
 */
const JURISDICTION_COLS: readonly Column[] = [
  ['Country', 'x2', '96px'],
  ['Gate', 'c', '150px'],
  ['onboarding_open', 'c', '146px'],
  ['passport_notified_at', 'm', '176px'],
  ['mandate_scope[]', 'x', 'minmax(240px,1.6fr)'],
  ['Open precondition', 'c', '214px'],
  ['Last gate change', 's', 'minmax(210px,1.2fr)'],
]

function tableFooter(rows: readonly RowRead[], unreadableRows: number): string {
  if (rows.length === 0) {
    // Two very different sentences. An empty list really is "we hold no
    // mandate anywhere", which is a safe state and a read of one. A list whose
    // every element this console failed to parse is an unread table, and
    // saying "nothing is open" over it is a fabricated all-clear on the screen
    // that answers whether we are trading inside our licence.
    return unreadableRows === 0
      ? 'No jurisdiction row exists. Fail-closed means nothing is open and no customer can onboard anywhere — this is a read of that state, not a failed read.'
      : `No row in this table could be read: ${String(unreadableRows)} row(s) came back in a shape this console does not know. This is an unread table, not an empty one.`
  }

  let open = 0
  let closed = 0
  let noMandate = 0
  let unknown = 0
  for (const row of rows) {
    if (row.gate === 'open') open += 1
    else if (row.gate === 'closed') closed += 1
    else if (row.gate === 'no_mandate') noMandate += 1
    else unknown += 1
  }

  // Every count is of rows whose gate was actually read. A row this console
  // could not resolve is counted in its own bucket rather than rounded into
  // "closed", because the reassuring bucket is the wrong place for a doubt.
  const parts = [`${String(open)} open`, `${String(closed)} closed`]
  if (noMandate > 0) parts.push(`${String(noMandate)} with no mandate row`)
  if (unknown > 0) parts.push(`${String(unknown)} whose gate could not be read`)
  if (unreadableRows > 0) parts.push(`${String(unreadableRows)} row(s) that could not be read at all`)
  return parts.join(' · ')
}

const FOOT2 = 'A country absent from this table has NO jurisdiction row — the third closed state, '
  + 'and the most common one: we hold no mandate there and the remedy is a mandate, not a toggle. '
  + 'This reports what we hold; it does not enumerate the countries we do not, and the per-country '
  + 'read answers for any of them. Closing a country needs no passport notification; opening one '
  + 'always does.'

// ---------------------------------------------------------------------------
// The fail-closed note
// ---------------------------------------------------------------------------

/**
 * What is blocked, what is contradictory, and what may not be said at all.
 *
 * The corpus note is a static paragraph about IT. This one is computed from the
 * rows that were read — and, more importantly, refuses to make the calm
 * statement when it was not able to check it. "No country is blocked on a
 * notification" over a table with two unreadable rows is exactly the shape of
 * reassurance this console exists not to produce.
 */
function failClosedNote(rows: readonly RowRead[], unreadableRows: number): NoteBlock {
  const blocked = rows
    .filter((r) => r.gate === 'closed' && r.notificationOnFile === false)
    .map((r) => r.country ?? '(country not stated)')
  const unlicensed = rows.filter((r) => r.openWithoutNotification)
    .map((r) => r.country ?? '(country not stated)')
  const emptyMandate = rows.filter((r) => r.openWithEmptyMandate)
    .map((r) => r.country ?? '(country not stated)')

  // The precise condition for the calm sentence: every row was an object, every
  // gate resolved, and every closed row said whether a notification is on file.
  // Anything less and the question was not answered, so it is not answered here.
  const checkedEverything = unreadableRows === 0
    && rows.every((r) => r.gate !== null && (r.gate !== 'closed' || r.notificationOnFile !== null))

  const parts: string[] = []

  if (unlicensed.length > 0) {
    parts.push(
      `${list(unlicensed)} ${unlicensed.length === 1 ? 'is' : 'are'} OPEN with no passport `
      + 'notification on file. The schema CHECK and AllowOnboarding both refuse that combination, '
      + 'so a row in this state means the gate and the licence basis disagree — and onboarding is '
      + 'being accepted on the wrong side of the disagreement.',
    )
  }

  if (emptyMandate.length > 0) {
    parts.push(
      `${list(emptyMandate)} ${emptyMandate.length === 1 ? 'is' : 'are'} open with an empty `
      + 'mandate_scope: onboarding is accepted there and every capability is refused, so customers '
      + 'can arrive and do nothing.',
    )
  }

  const closedRows = rows.filter((r) => r.gate === 'closed').length

  if (blocked.length > 0) {
    parts.push(
      `${list(blocked)} cannot be opened today: passport_notified_at is null, so adminops refuses `
      + 'the open outright — not a confirmation dialog, not an override for Admin. Record the '
      + 'passport notification first and the precondition is met.',
    )
  }

  // The caveat is ADDITIVE, not the alternative to a finding.
  //
  // It used to be the `else` of the branch above, so a table that named one
  // blocked country and failed to read two other rows said "IT cannot be opened
  // today" and nothing else — a finding that reads as THE finding, over rows
  // nobody checked. Naming what was found and admitting the search was partial
  // are two different sentences and the screen owes an operator both; the same
  // applies to the OPEN-without-notification and empty-mandate findings above,
  // which are likewise only over the rows that parsed.
  if (!checkedEverything) {
    parts.push(
      parts.length === 0
        ? 'Whether any closed country is blocked on a passport notification could NOT be '
          + 'established: some rows did not answer for their gate or their notification. Nothing '
          + 'here says none is blocked.'
        : 'Whether any other country is in one of these states could NOT be established: some rows '
          + 'did not answer for their gate or their notification, so everything above is only '
          + 'about the rows that did.',
    )
  } else if (blocked.length === 0) {
    // Counted, not phrased as though there were some. "Every closed country has
    // a notification on file" over a table containing none of them is a true
    // sentence that reads as a check having been performed on something.
    parts.push(rows.length === 0
      ? 'No jurisdiction row exists, so there is nothing here to be open, closed or blocked.'
      : closedRows === 0
        ? 'No country in this table is closed, so nothing here is waiting on a passport notification.'
        : 'No closed country in this table is blocked on a passport notification — every one of '
          + 'them has a date on file. That is checked against every row read here, not assumed.')
  }

  parts.push(
    'A capability absent from mandate_scope is refused even in an open country, so "open" is never '
    + 'the whole answer — the mandate column is the rest of it.',
  )

  const tone: Tone = unlicensed.length > 0 || emptyMandate.length > 0
    ? 'r'
    : blocked.length > 0 || !checkedEverything ? 'a' : 'n'

  return {
    t: 'note',
    tone,
    kicker: 'Fail closed',
    title: 'Closed is three states, and they are not interchangeable',
    body: parts.join(' '),
    ref: 'invariant 7 · no row · onboarding_open false · capability outside mandate_scope',
  }
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * How a refusal is quoted, and what is said when it cannot be.
 *
 * Law 13 — every refusal surfaces a `decision_ref`. This read is behind
 * `PermJurisdictionRead`, which the backend deliberately scopes to Compliance
 * rather than to every operator, so a 403 here is the expected experience for
 * an ops analyst and the note has to be usable by one. A refusal arriving
 * without a reference leaves them nothing to put in an escalation, and the
 * console says exactly that rather than inventing one.
 */
function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref, so there is nothing to quote in an escalation.'
  }
  return fallback
}

/**
 * The screen when the list itself could not be read.
 *
 * One note and nothing else. The alternative — falling back to the design
 * corpus — would put seven invented countries, four invented open markets and
 * an invented mandate scope on the screen a compliance officer reads to answer
 * "are we accepting onboarding where we are licensed to". That answer being
 * fictional is worse than it being absent.
 *
 * The provenance is `unread`, not `partial`: the chip renders one word, and
 * "Partial" says some of this is real. Nothing here is.
 */
function unreadableScreen(failed: Extract<Attempt, { ok: false }>): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Jurisdictions & country gating',
      title: 'This screen could not be read',
      body: `${failed.reason} No country, gate, mandate scope or passport notification below is `
        + 'known. That is not the same as nothing being open, and not the same as everything being '
        + 'open — it is the absence of an answer, and the screen shows none rather than guessing '
        + 'at any.',
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source: SOURCE, reason: failed.reason },
  }
}

/**
 * Folds the list and the per-country histories into the blocks the screen
 * renders.
 *
 * Pure, and separate from the fetch, so every absence path — an unknown gate, a
 * notification flag with no date, a history read that answered for the wrong
 * country — is table-testable without a client (CLAUDE.md Part III).
 *
 * `at` is the instant the console received the answers. It is a parameter
 * because a pure function does not read a clock, and it is labelled a receipt
 * on screen because neither endpoint says when it read its rows.
 */
export function composeJurisdictions(
  listRead: Attempt,
  details: ReadonlyMap<string, Attempt>,
  at: Date,
  controls?: ControlBuilder,
): ScreenData {
  if (!listRead.ok) return unreadableScreen(listRead)

  const envelope = asRecord(listRead.payload)
  const raw = envelope === null ? null : asArray(envelope['jurisdictions'])
  if (raw === null) {
    return unreadableScreen({
      ok: false,
      reason: 'The jurisdictions list returned a shape this console does not know.',
      decisionRef: null,
      refused: false,
    })
  }

  const rows: RowRead[] = []
  let unreadableRows = 0
  for (const entry of raw) {
    // The backend orders by country_code. Re-sorting here would silently
    // disagree with the ordering the endpoint documents and tests.
    const row = readJurisdiction(entry, details)
    if (row === null) unreadableRows += 1
    else rows.push(row)
  }

  const gaps = summarise(rows.flatMap((r) => r.gaps))
  const missing = [...gaps]
  if (unreadableRows > 0) {
    missing.push(`${String(unreadableRows)} jurisdiction row(s) this console could not read`)
  }

  const table: TableBlock = {
    t: 'table',
    title: 'jurisdictions',
    note: 'Ordered by country code by the backend; this console does not re-sort. The table itself is read-only: declaring, opening, closing and moving a mandate are separately permissioned acts, and each has its own control below the fail-closed note.',
    // The corpus draws onboarding_open=true, passport_notified=false and
    // mandate=cash_out as applied facets. This read applies no filter at all,
    // and a facet chip is a claim that one is applied — on a gating table, a
    // claim that the list is longer than what is shown.
    facets: false,
    cols: JURISDICTION_COLS,
    rows: rows.map((r) => r.cells),
    footer: tableFooter(rows, unreadableRows),
    foot2: FOOT2,
  }

  const blocks: Block[] = [table]

  if (missing.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Not shown',
      title: 'Some fields on this table could not be read',
      body: missing.join(' · '),
      ref: 'A field that could not be read is not a field of "no", and an unread gate is not a closed one.',
    })
  }

  blocks.push(failClosedNote(rows, unreadableRows))

  // The controls go AFTER the fail-closed note, deliberately. The note is what
  // says which countries cannot be opened and why; an operator who scrolls to
  // the open control has passed it on the way. Putting the forms above it would
  // let somebody reach for the act before reading the constraint on it.
  if (controls !== undefined) blocks.push(...controls(rows))

  const readAt = `${readAtStamp(at)} (console receipt — neither endpoint states when it read)`
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: SOURCE, authority: 'operational', readAt }
    : { kind: 'partial', source: SOURCE, authority: 'operational', readAt, missing }

  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * The alpha-2 codes worth a per-country read, from a list payload.
 *
 * Exported so the fan-out is table-testable on its own. Rows with no country,
 * a country that is not alpha-2, or a duplicate are skipped — the backend 400s
 * on the first two, and issuing a request known to fail buys a noisier screen
 * and nothing else. Their history cells say "not requested" rather than
 * pretending there is none.
 */
export function countriesOf(payload: unknown, cap: number = DETAIL_READ_CAP): readonly string[] {
  const envelope = asRecord(payload)
  const raw = envelope === null ? null : asArray(envelope['jurisdictions'])
  if (raw === null) return []

  const out: string[] = []
  const seen = new Set<string>()
  for (const entry of raw) {
    if (out.length >= cap) break
    const row = asRecord(entry)
    if (row === null) continue
    const country = readText(row, 'country')
    if (country === null) continue
    const code = normaliseCountry(country)
    if (!isAlpha2(code) || seen.has(code)) continue
    seen.add(code)
    out.push(code)
  }
  return out
}

/**
 * One read, resolved into an answer either way.
 *
 * An `AbortError` is rethrown: a navigation away is a cancellation, not a
 * degraded screen, and swallowing it here would render "the gate could not be
 * read" over a screen the operator has already left.
 */
async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return { ok: false, reason: `${subject} could not be reached.`, decisionRef: null, refused: false }
  }
}

/**
 * Reads the gating table, then each country's gate history.
 *
 * Sequential between the two stages and parallel within the second, because the
 * second stage's inputs are the first stage's answer. The list is the screen —
 * if it fails there is nothing to fan out from, and no history read is issued
 * at all rather than being issued and thrown away.
 *
 * Resolves rather than throwing, per the loader contract: a backend that said
 * "I don't know" is a `partial` the operator can read, not an exception that
 * collapses the screen to a stack trace.
 */
export const loadJurisdictions: ScreenLoader = async (api) => {
  // Started before the list read and never left dangling — see `callerBeside`.
  // An aborted navigation aborts both, and an unawaited rejection here would be
  // a crash report with no failing test behind it.
  const calling = callerBeside(api)

  const listRead = await attempt(() => api.get<unknown>(JURISDICTIONS_PATH), 'The jurisdictions list')

  const details = new Map<string, Attempt>()
  if (listRead.ok) {
    const codes = countriesOf(listRead.payload)
    const answers = await Promise.all(codes.map(async (code) => [
      code,
      await attempt(
        () => api.get<unknown>(jurisdictionPath(code)),
        `The gate history for ${code}`,
      ),
    ] as const))
    for (const [code, answer] of answers) details.set(code, answer)
  }

  // The controls are built from the caller's own authority and appended by
  // `composeJurisdictions` from the rows it parsed. A list read that failed
  // returns the unreadable-screen note and never reaches the builder: offering
  // to open a country over a table nobody could read would be offering to act
  // on a gate state this console does not have.
  return composeJurisdictions(
    listRead, details, new Date(), jurisdictionControls(api, await calling),
  )
}
